// ============================================================================
// THE CYCLE — Spray Chart Explorer (Reusable)
// Batted ball distribution with Statcast summary, park-specific outfield walls,
// and pitch-type breakdown. Usable standalone (with search) or embedded
// in PlayerDetail (with pre-selected player).
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, Typography, Grid, Autocomplete, TextField, Avatar,
  useTheme, alpha, ToggleButton, ToggleButtonGroup,
  Chip, Skeleton, Tooltip, LinearProgress, MenuItem, Select, FormControl,
} from '@mui/material';
import { ScatterPlot, Close, Stadium } from '@mui/icons-material';
import AtBatDetailDialog from './AtBatDetailDialog';
import { motion } from 'framer-motion';
import { pitchSplitsApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { themeUtils } from '../theme/theme';
import SprayChart from './charts/SprayChart';
import { getPitchName as pitchLabel, PITCH_COLORS } from '../utils/pitchTypes';

// ── MLB Park Dimensions ──
// [LF line, LF power alley, LCF, CF, RCF, RF power alley, RF line] in feet
// Angles: [-45°, -30°, -15°, 0°, 15°, 30°, 45°]
const MLB_PARKS = {
  AZ:  { name: 'Chase Field',        dims: [330, 374, 413, 407, 413, 374, 334] },
  ARI: { name: 'Chase Field',        dims: [330, 374, 413, 407, 413, 374, 334] },
  ATL: { name: 'Truist Park',        dims: [335, 385, 400, 400, 375, 375, 325] },
  BAL: { name: 'Camden Yards',       dims: [333, 364, 388, 410, 373, 373, 318] },
  BOS: { name: 'Fenway Park',        dims: [310, 379, 390, 420, 380, 380, 302] },
  CHC: { name: 'Wrigley Field',      dims: [355, 368, 368, 400, 368, 368, 353] },
  CWS: { name: 'Guaranteed Rate',    dims: [330, 375, 375, 400, 375, 375, 335] },
  CHW: { name: 'Guaranteed Rate',    dims: [330, 375, 375, 400, 375, 375, 335] },
  CIN: { name: 'Great American BP',  dims: [328, 379, 379, 404, 370, 370, 325] },
  CLE: { name: 'Progressive Field',  dims: [325, 370, 370, 400, 375, 375, 325] },
  COL: { name: 'Coors Field',        dims: [347, 390, 390, 415, 375, 375, 350] },
  DET: { name: 'Comerica Park',      dims: [345, 370, 370, 420, 365, 365, 330] },
  HOU: { name: 'Minute Maid Park',   dims: [315, 362, 362, 409, 373, 373, 326] },
  KC:  { name: 'Kauffman Stadium',   dims: [330, 387, 387, 410, 387, 387, 330] },
  LAA: { name: 'Angel Stadium',      dims: [330, 387, 387, 400, 370, 370, 330] },
  LAD: { name: 'Dodger Stadium',     dims: [330, 375, 375, 395, 375, 375, 330] },
  MIA: { name: 'loanDepot Park',     dims: [344, 386, 386, 407, 392, 392, 335] },
  MIL: { name: 'American Family',    dims: [344, 371, 371, 400, 374, 374, 345] },
  MIN: { name: 'Target Field',       dims: [339, 377, 377, 404, 367, 367, 328] },
  NYM: { name: 'Citi Field',         dims: [335, 379, 379, 408, 383, 383, 330] },
  NYY: { name: 'Yankee Stadium',     dims: [318, 399, 399, 408, 385, 385, 314] },
  OAK: { name: 'Coliseum',           dims: [330, 362, 362, 400, 362, 362, 330] },
  ATH: { name: 'Coliseum/Sacramento',dims: [330, 362, 362, 400, 362, 362, 330] },
  PHI: { name: 'Citizens Bank Park', dims: [329, 374, 374, 401, 369, 369, 330] },
  PIT: { name: 'PNC Park',           dims: [325, 389, 389, 399, 375, 375, 320] },
  SD:  { name: 'Petco Park',         dims: [336, 390, 390, 396, 391, 391, 322] },
  SF:  { name: 'Oracle Park',        dims: [339, 364, 364, 399, 421, 421, 309] },
  SEA: { name: 'T-Mobile Park',      dims: [331, 378, 378, 401, 381, 381, 326] },
  STL: { name: 'Busch Stadium',      dims: [336, 375, 375, 400, 375, 375, 335] },
  TB:  { name: 'Tropicana Field',    dims: [315, 370, 370, 404, 370, 370, 322] },
  TEX: { name: 'Globe Life Field',   dims: [329, 372, 372, 407, 374, 374, 326] },
  TOR: { name: 'Rogers Centre',      dims: [328, 375, 375, 400, 375, 375, 328] },
  WSH: { name: 'Nationals Park',     dims: [336, 377, 377, 402, 370, 370, 335] },
  WSN: { name: 'Nationals Park',     dims: [336, 377, 377, 402, 370, 370, 335] },
};

/** Look up park data, normalizing team code */
const getParkData = (teamCode) => {
  if (!teamCode) return null;
  return MLB_PARKS[teamCode.toUpperCase()] || null;
};

// ── Constants ──

const GRADE_COLORS = {
  'A+': '#00C851', A: '#4CAF50', 'B+': '#8BC34A', B: '#CDDC39',
  'B-': '#FFEB3B', C: '#FF9800', 'C-': '#FF5722', D: '#F44336', F: '#D32F2F',
};

const getStatcastGrade = (metric, value) => {
  const v = parseFloat(value);
  if (isNaN(v)) return null;
  const tiers = {
    avgEV:         [[93,'A+'],[91,'A'],[89.5,'B+'],[88,'B'],[86.5,'C'],[85,'D'],[0,'F']],
    hardHitRate:   [[48,'A+'],[42,'A'],[37,'B+'],[33,'B'],[28,'C'],[22,'D'],[0,'F']],
    barrelRate:    [[15,'A+'],[11,'A'],[8.5,'B+'],[6.5,'B'],[4.5,'C'],[2.5,'D'],[0,'F']],
    lineDriveRate: [[28,'A+'],[25,'A'],[22,'B+'],[20,'B'],[17,'C'],[14,'D'],[0,'F']],
  };
  const t = tiers[metric];
  if (!t) return null;
  for (const [min, grade] of t) { if (v >= min) return grade; }
  return 'F';
};

const GradeDot = ({ grade }) => {
  if (!grade) return null;
  return (
    <Tooltip title={`Grade: ${grade}`} placement="top" arrow>
      <Box component="span" sx={{
        display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
        bgcolor: GRADE_COLORS[grade] || '#666', ml: 0.5, verticalAlign: 'middle',
      }} />
    </Tooltip>
  );
};

const normalizeOutcome = (raw) => (raw || '').toLowerCase().replace(/_/g, ' ').trim();
const isHitOutcome = (raw) => {
  const value = normalizeOutcome(raw);
  return value === 'single' || value === 'double' || value === 'triple' || value === 'home run';
};
const matchesOutcomeFilter = (raw, filter) => {
  const value = normalizeOutcome(raw);
  if (!filter || filter === 'all') return true;
  if (filter === 'hits') return isHitOutcome(value);
  if (filter === 'outs') return !isHitOutcome(value);
  if (filter === 'home_run') return value === 'home run';
  return value === filter;
};

/**
 * SprayChartExplorer
 * @param {Object}  player      — Pre-selected player { team, name, season, bats }. If provided, search UI is hidden.
 * @param {string}  perspective — 'batter' | 'pitcher'. Defaults to 'batter'.
 * @param {boolean} compact     — If true, hides the page header for embedded use.
 */
const SprayChartExplorer = ({ player: playerProp, perspective: perspectiveProp, compact = false, onBallClick }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(playerProp || null);
  const [perspective, setPerspective] = useState(perspectiveProp || 'batter');
  const [pitchData, setPitchData] = useState(null);
  const [rawEvents, setRawEvents] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [handFilter, setHandFilter] = useState('all');
  const [seasonType, setSeasonType] = useState('R');
  const [venueFilter, setVenueFilter] = useState('all');
  const [availableVenues, setAvailableVenues] = useState([]);
  const [vsTeamFilter, setVsTeamFilter] = useState('all');
  const [vsPitcherFilter, setVsPitcherFilter] = useState('all');
  const [availableOpponents, setAvailableOpponents] = useState([]);
  const [availablePitchers, setAvailablePitchers] = useState([]);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [abDialogOpen, setAbDialogOpen] = useState(false);
  const [abDialogLoading, setAbDialogLoading] = useState(false);
  const [abDialogError, setAbDialogError] = useState('');
  const [selectedAtBat, setSelectedAtBat] = useState(null);
  const allBattedRef = useRef([]);

  const isEmbedded = !!playerProp;

  // Sync external player prop changes
  useEffect(() => {
    if (playerProp) {
      setSelectedPlayer(playerProp);
    }
  }, [playerProp?.team, playerProp?.name, playerProp?.season]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external perspective prop
  useEffect(() => {
    if (perspectiveProp) setPerspective(perspectiveProp);
  }, [perspectiveProp]);

  // Debounced search (only when not embedded)
  useEffect(() => {
    if (isEmbedded) return;
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await pitchSplitsApi.searchPitchPlayers(searchQuery, { perspective, limit: 20 });
        setSearchResults(res?.players || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, perspective, isEmbedded]);

  // Load pitch data when player selected
  useEffect(() => {
    if (!selectedPlayer) return;
    const load = async () => {
      setDataLoading(true);
      setVenueFilter('all'); // reset venue filter on new data load
      try {
        const team = selectedPlayer.team;
        const name = selectedPlayer.name || selectedPlayer.playerName;
        const season = selectedPlayer.season || new Date().getFullYear();
        const filters = { limit: 5000 };
        if (seasonType && seasonType !== 'all') filters.gameType = seasonType;
        const res = await pitchSplitsApi.getPitchEvents(perspective, team, name, season, filters);
        // Derive venue for each event & collect unique venues
        const events = res?.events || [];
        const venueSet = new Set();
        events.forEach(e => {
          e._venue = e.homeAway === 'home' ? (selectedPlayer.team || '').toUpperCase() : (e.opponent || '').toUpperCase();
          if (e._venue) venueSet.add(e._venue);
        });
        const sortedVenues = [...venueSet].sort((a, b) => {
          const pn = (n) => getParkData(n)?.name || n;
          return pn(a).localeCompare(pn(b));
        });
        setAvailableVenues(sortedVenues);
        // Store all batted events for re-filtering
        const batted = events.filter(e => e.exitVelocity && e.launchAngle != null);
        allBattedRef.current = batted;
        // Collect unique opponents and pitchers
        const oppSet = new Set();
        const pitcherSet = new Set();
        batted.forEach(e => {
          if (e.opponent) oppSet.add(e.opponent.toUpperCase());
          if (e.opponentName) pitcherSet.add(e.opponentName);
        });
        setAvailableOpponents([...oppSet].sort());
        setAvailablePitchers([...pitcherSet].sort((a, b) => a.split(' ').pop().localeCompare(b.split(' ').pop())));
        setVsTeamFilter('all');
        setVsPitcherFilter('all');
        setOutcomeFilter('all');
        setHandFilter('all');
        processBattedBallData(batted, 'all', 'all', 'all', 'all', 'all');
      } catch (err) {
        console.error('Failed to load pitch data:', err);
        setPitchData(null);
        setRawEvents([]);
        setAvailableVenues([]);
        allBattedRef.current = [];
      } finally {
        setDataLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer, perspective, seasonType]);

  // Re-process when client-side filters change (no new API call needed)
  useEffect(() => {
    if (!selectedPlayer || allBattedRef.current.length === 0) return;
    processBattedBallData(allBattedRef.current, venueFilter, vsTeamFilter, vsPitcherFilter, outcomeFilter, handFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueFilter, vsTeamFilter, vsPitcherFilter, outcomeFilter, handFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processBattedBallData = useCallback((allBatted, venue, vsTeam, vsPitcher, outcome, hand) => {
    let batted = venue && venue !== 'all'
      ? allBatted.filter(e => e._venue === venue)
      : [...allBatted];
    if (vsTeam && vsTeam !== 'all') batted = batted.filter(e => (e.opponent || '').toUpperCase() === vsTeam);
    if (vsPitcher && vsPitcher !== 'all') batted = batted.filter(e => e.opponentName === vsPitcher);
    if (outcome && outcome !== 'all') batted = batted.filter(e => matchesOutcomeFilter(e.atBatResult, outcome));
    if (hand && hand !== 'all') batted = batted.filter(e => (e.opponentHand || '').toUpperCase() === hand.toUpperCase());
    if (batted.length === 0) { setPitchData({ total: 0 }); setRawEvents([]); return; }

    let groundBall = 0, flyBall = 0, lineDrive = 0, popup = 0;
    let totalEV = 0, totalLA = 0, totalDist = 0, distCount = 0, hardHit = 0, barrel = 0;
    const spray = { pull: { count: 0, hits: 0 }, center: { count: 0, hits: 0 }, oppo: { count: 0, hits: 0 } };
    const byPitchType = {};

    batted.forEach(e => {
      const la = parseFloat(e.launchAngle), ev = parseFloat(e.exitVelocity);
      const dist = parseFloat(e.distance) || 0;
      const traj = (e.trajectory || '').toLowerCase();
      const isHit = ['single', 'double', 'triple', 'home_run', 'home run'].includes((e.atBatResult || '').toLowerCase());

      if (traj.includes('ground') || la < 10) groundBall++;
      else if (traj.includes('line') || (la >= 10 && la <= 25)) lineDrive++;
      else if (traj.includes('fly') || (la > 25 && la <= 50)) flyBall++;
      else if (traj.includes('popup') || la > 50) popup++;
      else flyBall++;

      totalEV += ev; totalLA += la;
      if (dist) { totalDist += dist; distCount++; }
      if (ev >= 95) hardHit++;
      if (ev >= 98 && la >= 26 && la <= 30) barrel++;

      const zone = e.zone || 0;
      const hand = selectedPlayer?.bats || 'R';
      // Zone grid (catcher's view): col 0=left(3B side), col 1=center, col 2=right(1B side)
      // Left col (zones 1,4,7): inside for RHB → pull; inside for LHB → not (those are right col)
      // Right col (zones 3,6,9): inside for LHB → pull
      const col = zone >= 1 && zone <= 9 ? (zone - 1) % 3 : 1;
      let dir;
      if (col === 0) dir = hand === 'R' ? 'pull' : 'oppo';   // left column = 3B side
      else if (col === 1) dir = 'center';                      // center column
      else dir = hand === 'R' ? 'oppo' : 'pull';              // right column = 1B side
      spray[dir].count++; if (isHit) spray[dir].hits++;

      const pt = e.pitchType || 'UK';
      if (!byPitchType[pt]) byPitchType[pt] = { count: 0, totalEV: 0, totalLA: 0, hardHit: 0 };
      byPitchType[pt].count++; byPitchType[pt].totalEV += ev;
      byPitchType[pt].totalLA += la; if (ev >= 95) byPitchType[pt].hardHit++;
    });

    const total = batted.length;

    setRawEvents(batted.map(e => {
      const la = parseFloat(e.launchAngle);
      const ev = parseFloat(e.exitVelocity);
      const dist = parseFloat(e.distance) || 0;
      const zone = e.zone || 0;
      const hand = selectedPlayer?.bats || 'R';
      const result = (e.atBatResult || '').toLowerCase();
      // Spray angle: based on column position in strike zone (field-oriented, not batter-relative)
      // col 0 (zones 1,4,7) = left/3B side → LF = negative angle
      // col 1 (zones 2,5,8) = center → CF = ~0
      // col 2 (zones 3,6,9) = right/1B side → RF = positive angle
      // This is already field-oriented so no handedness flip needed on angle.
      let sprayAngle;
      if (zone >= 1 && zone <= 9) {
        const col = (zone - 1) % 3;  // 0=left, 1=center, 2=right
        sprayAngle = (col - 1) * 35; // -35, 0, +35
      } else {
        sprayAngle = (Math.random() - 0.5) * 70;
      }
      sprayAngle += (Math.random() - 0.5) * 12;
      const normDist = dist > 0 ? Math.min(dist / 450, 1) : (0.25 + Math.random() * 0.35);
      return { launchAngle: la, exitVelocity: ev, distance: dist, sprayAngle, normDist, result, zone,
        gameDate: e.gameDate || null, opponent: e.opponent || null, opponentName: e.opponentName || null,
        inning: e.inning || null, gameId: e.gameId || null, atBatResult: e.atBatResult || null };
    }));

    setPitchData({
      total, spray, groundBall, flyBall, lineDrive, popup,
      groundBallRate: ((groundBall / total) * 100).toFixed(1),
      flyBallRate: ((flyBall / total) * 100).toFixed(1),
      lineDriveRate: ((lineDrive / total) * 100).toFixed(1),
      avgExitVelo: (totalEV / total).toFixed(1),
      avgLaunchAngle: (totalLA / total).toFixed(1),
      avgDistance: distCount > 0 ? (totalDist / distCount).toFixed(0) : null,
      hardHitRate: ((hardHit / total) * 100).toFixed(1),
      barrelRate: ((barrel / total) * 100).toFixed(1),
      byPitchType: Object.fromEntries(
        Object.entries(byPitchType).map(([pt, d]) => [pt, {
          count: d.count,
          avgEV: (d.totalEV / d.count).toFixed(1),
          avgLA: (d.totalLA / d.count).toFixed(1),
          hardHitPct: ((d.hardHit / d.count) * 100).toFixed(1),
        }])
      ),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer]);

  const handleDotClick = useCallback(async (event) => {
    if (!event || !selectedPlayer) return;
    setAbDialogOpen(true);
    setAbDialogLoading(true);
    setAbDialogError('');
    setSelectedAtBat(null);
    try {
      const team = selectedPlayer.team;
      const name = selectedPlayer.name || selectedPlayer.playerName;
      const season = selectedPlayer.season || new Date().getFullYear();
      const filters = { page: 1, pageSize: 50 };
      if (seasonType && seasonType !== 'all') filters.gameType = seasonType;
      if (event.gameDate) filters.gameDate = event.gameDate;
      if (event.opponent) filters.opponent = event.opponent;
      if (event.inning) filters.inning = event.inning;
      if (event.opponentName) filters.vsPlayer = event.opponentName;

      const response = await pitchSplitsApi.getAtBats(perspective, team, name, season, filters);
      const atBats = response?.atBats || [];
      let match = atBats.find((ab) =>
        (!event.gameId || ab.gameId === event.gameId) &&
        (!event.gameDate || ab.gameDate === event.gameDate) &&
        (!event.opponent || ab.opponent === event.opponent) &&
        (!event.inning || ab.inning === event.inning)
      );
      if (!match && event.opponentName) {
        match = atBats.find((ab) =>
          (!event.gameDate || ab.gameDate === event.gameDate) &&
          (!event.opponent || ab.opponent === event.opponent) &&
          ab.opponentName === event.opponentName
        );
      }
      if (!match) match = atBats[0] || null;

      if (!match) {
        setAbDialogError('No matching at-bat found for this batted ball.');
      } else {
        setSelectedAtBat(match);
      }
      if (onBallClick) onBallClick(event);
    } catch (error) {
      setAbDialogError('Failed to load at-bat details.');
    } finally {
      setAbDialogLoading(false);
    }
  }, [selectedPlayer, seasonType, perspective, onBallClick]);

  const teamColor = selectedPlayer
    ? (themeUtils.getTeamColor(selectedPlayer.team) || theme.palette.primary.main)
    : theme.palette.primary.main;

  const StatTile = ({ label, value, suffix = '', highlight, color, grade }) => (
    <Box sx={{
      textAlign: 'center', px: 1.5, py: 0.75, minWidth: 64,
      borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
      '&:last-child': { borderRight: 'none' },
      position: 'relative',
    }}>
      <Typography variant="caption" color="text.secondary" sx={{
        fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em',
        fontWeight: 600, display: 'block', mb: 0.25,
      }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
        <Typography variant="body2" fontWeight={800} fontFamily="monospace" sx={{
          fontSize: '0.9rem', color: highlight ? (color || '#2ecc71') : 'text.primary',
        }}>
          {value}{suffix && <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{suffix}</Typography>}
        </Typography>
        <GradeDot grade={grade} />
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Header — hidden when compact/embedded */}
      {!compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScatterPlot sx={{ fontSize: 24, color: theme.palette.primary.main }} />
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.15rem', sm: '1.4rem' } }}>
            Spray Chart Explorer
          </Typography>
        </Box>
      )}

      {/* Search & Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {!isEmbedded && (
          <>
            <Autocomplete
              options={searchResults}
              getOptionLabel={(o) => `${o.name || o.playerName} (${o.team})`}
              loading={searchLoading}
              onInputChange={(_, val) => setSearchQuery(val)}
              onChange={(_, val) => setSelectedPlayer(val)}
              renderOption={(props, opt) => (
                <li {...props} key={`${opt.team}-${opt.name}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={getTeamLogoUrl(opt.team)} sx={{ width: 20, height: 20 }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                    <Typography variant="body2" fontWeight={600}>{opt.name}</Typography>
                    <Chip label={opt.team} size="small" sx={{ height: 18, fontSize: '0.75rem' }} />
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search player..." size="small" />
              )}
              size="small"
              sx={{ minWidth: 220, flex: 1, maxWidth: 340 }}
            />
            <ToggleButtonGroup value={perspective} exclusive size="small"
              onChange={(_, v) => { if (v) { setPerspective(v); setSelectedPlayer(null); setPitchData(null); } }}
            >
              <ToggleButton value="batter" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5 }}>Batter</ToggleButton>
              <ToggleButton value="pitcher" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5 }}>Pitcher</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
        <ToggleButtonGroup value={seasonType} exclusive size="small"
          onChange={(_, v) => { if (v) setSeasonType(v); }}
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>All</ToggleButton>
          <ToggleButton value="S" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>🌴 Spring</ToggleButton>
          <ToggleButton value="R" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>⚾ Reg</ToggleButton>
          <ToggleButton value="P" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>🏆 Post</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup value={handFilter} exclusive size="small"
          onChange={(_, v) => { if (v) setHandFilter(v); }}
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>All</ToggleButton>
          <ToggleButton value="R" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>vs R</ToggleButton>
          <ToggleButton value="L" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1 }}>vs L</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            displayEmpty
            sx={{
              fontSize: '0.75rem', fontWeight: 600, height: 32,
              '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Outcomes</MenuItem>
            <MenuItem value="hits" sx={{ fontSize: '0.75rem' }}>Hits</MenuItem>
            <MenuItem value="outs" sx={{ fontSize: '0.75rem' }}>Outs</MenuItem>
            <MenuItem value="single" sx={{ fontSize: '0.75rem' }}>Singles</MenuItem>
            <MenuItem value="double" sx={{ fontSize: '0.75rem' }}>Doubles</MenuItem>
            <MenuItem value="triple" sx={{ fontSize: '0.75rem' }}>Triples</MenuItem>
            <MenuItem value="home_run" sx={{ fontSize: '0.75rem' }}>Home Runs</MenuItem>
          </Select>
        </FormControl>
        {/* vs Team filter */}
        {availableOpponents.length > 0 && selectedPlayer && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={vsTeamFilter}
              onChange={(e) => setVsTeamFilter(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.75rem', fontWeight: 600, height: 32,
                '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 },
              }}
              renderValue={(v) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {v !== 'all' && (
                    <Avatar src={getTeamLogoUrl(v)} sx={{ width: 14, height: 14 }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                  )}
                  {v === 'all' ? 'vs Team' : v}
                </Box>
              )}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Teams</MenuItem>
              {availableOpponents.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: '0.75rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Avatar src={getTeamLogoUrl(t)} sx={{ width: 16, height: 16 }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                    {t}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* vs Pitcher filter (batter perspective only) */}
        {availablePitchers.length > 0 && selectedPlayer && perspective === 'batter' && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={vsPitcherFilter}
              onChange={(e) => setVsPitcherFilter(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.75rem', fontWeight: 600, height: 32,
                '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 },
              }}
              renderValue={(v) => v === 'all' ? 'vs Pitcher' : v}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Pitchers</MenuItem>
              {availablePitchers.map(p => (
                <MenuItem key={p} value={p} sx={{ fontSize: '0.75rem' }}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Venue / Stadium filter */}
        {availableVenues.length > 0 && selectedPlayer && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.75rem', fontWeight: 600, height: 32,
                '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 },
              }}
              renderValue={(v) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Stadium sx={{ fontSize: 14, opacity: 0.7 }} />
                  {v === 'all' ? 'All Parks' : (getParkData(v)?.name || v)}
                </Box>
              )}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Parks</MenuItem>
              {availableVenues.map(v => (
                <MenuItem key={v} value={v} sx={{ fontSize: '0.75rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Avatar src={getTeamLogoUrl(v)} sx={{ width: 16, height: 16 }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                    {getParkData(v)?.name || v}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Player Header (search-based only) */}
      {selectedPlayer && !isEmbedded && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 2,
          px: 1.5, py: 0.6, borderRadius: 2,
          border: `1px solid ${alpha(teamColor, 0.2)}`,
          bgcolor: alpha(teamColor, 0.03),
        }}>
          <Avatar src={getTeamLogoUrl(selectedPlayer.team)} sx={{ width: 22, height: 22 }}
            imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
          <Typography variant="body2" fontWeight={700}>{selectedPlayer.name}</Typography>
          <Chip label={selectedPlayer.team} size="small" sx={{
            height: 20, fontSize: '0.75rem', fontWeight: 600,
            bgcolor: alpha(teamColor, 0.1), color: teamColor,
          }} />
          <Chip label={perspective} size="small" variant="outlined"
            sx={{ height: 20, fontSize: '0.75rem', textTransform: 'capitalize' }} />
          {seasonType !== 'all' && (
            <Chip label={seasonType === 'S' ? 'Spring' : seasonType === 'P' ? 'Postseason' : 'Regular'}
              size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
          )}
          {handFilter !== 'all' && (
            <Chip label={`vs ${handFilter}HP`} size="small" variant="outlined"
              sx={{ height: 20, fontSize: '0.75rem' }} />
          )}
          <Box sx={{ flex: 1 }} />
          <Chip label="Clear" size="small" variant="outlined"
            icon={<Close sx={{ fontSize: '14px !important' }} />}
            onClick={() => { setSelectedPlayer(null); setPitchData(null); }}
            sx={{ height: 22, fontSize: '0.75rem', cursor: 'pointer' }}
          />
        </Box>
      )}

      {/* Loading Skeleton */}
      {dataLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
        </Box>
      )}

      {/* Results */}
      {!dataLoading && pitchData && selectedPlayer && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
          {pitchData.total > 0 && (
            <>
              {/* Statcast Summary Bar */}
              <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{
                  py: 0.5, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.75,
                  background: `linear-gradient(135deg, ${alpha(teamColor, 0.85)}, ${alpha(teamColor, 0.65)})`,
                  color: 'white',
                }}>
                  <ScatterPlot sx={{ fontSize: 14 }} />
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>Statcast Summary</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    {pitchData.total} batted balls{venueFilter !== 'all' ? ` @ ${getParkData(venueFilter)?.name || venueFilter}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', overflowX: 'auto' }}>
                  <StatTile label="Avg EV" value={pitchData.avgExitVelo} suffix=" mph"
                    highlight={parseFloat(pitchData.avgExitVelo) >= 90}
                    grade={getStatcastGrade('avgEV', pitchData.avgExitVelo)} />
                  <StatTile label="Avg LA" value={`${pitchData.avgLaunchAngle}°`} />
                  {pitchData.avgDistance && <StatTile label="Avg Dist" value={pitchData.avgDistance} suffix=" ft" />}
                  <StatTile label="Hard Hit%" value={`${pitchData.hardHitRate}%`}
                    highlight={parseFloat(pitchData.hardHitRate) >= 40}
                    grade={getStatcastGrade('hardHitRate', pitchData.hardHitRate)} />
                  <StatTile label="Barrel%" value={`${pitchData.barrelRate}%`}
                    highlight={parseFloat(pitchData.barrelRate) >= 8} color="#e74c3c"
                    grade={getStatcastGrade('barrelRate', pitchData.barrelRate)} />
                  <StatTile label="GB%" value={`${pitchData.groundBallRate}%`} />
                  <StatTile label="LD%" value={`${pitchData.lineDriveRate}%`}
                    highlight={parseFloat(pitchData.lineDriveRate) >= 22}
                    grade={getStatcastGrade('lineDriveRate', pitchData.lineDriveRate)} />
                  <StatTile label="FB%" value={`${pitchData.flyBallRate}%`} />
                </Box>
              </Card>

              {/* Spray Chart + Pitch Type Breakdown */}
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={7}>
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                    <SprayChart battedBall={pitchData} batterHand={selectedPlayer.bats || 'R'} perspective={perspective} rawEvents={rawEvents} parkDimensions={venueFilter !== 'all' ? getParkData(venueFilter)?.dims : null} parkName={venueFilter !== 'all' ? getParkData(venueFilter)?.name : null} onDotClick={handleDotClick} />
                  </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                  {pitchData.byPitchType && Object.keys(pitchData.byPitchType).length > 0 && (
                    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{
                        py: 0.5, px: 1.5, display: 'flex', alignItems: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        color: 'white',
                      }}>
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>Batted Balls by Pitch Type</Typography>
                      </Box>
                      {/* Column headers */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, py: 0.4, px: 1,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                        bgcolor: alpha(theme.palette.divider, 0.03),
                      }}>
                        <Box sx={{ width: 7, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.secondary', flex: 1, minWidth: 70 }}>Pitch</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.secondary', width: 24, textAlign: 'right' }}>#</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.secondary', width: 34, textAlign: 'right' }}>EV</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.secondary', width: 28, textAlign: 'right' }}>LA</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.secondary', width: 34, textAlign: 'right' }}>HH%</Typography>
                      </Box>
                      <Box sx={{ p: 0.5, flex: 1 }}>
                        {Object.entries(pitchData.byPitchType)
                          .sort((a, b) => b[1].count - a[1].count)
                          .map(([pt, d], idx, arr) => {
                            const maxCount = Math.max(...Object.values(pitchData.byPitchType).map(v => v.count));
                            const pct = (d.count / maxCount) * 100;
                            const hhGrade = getStatcastGrade('hardHitRate', d.hardHitPct);
                            return (
                              <Box key={pt} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 0.5,
                                borderBottom: idx < arr.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.08)}` : 'none',
                                '&:hover': { bgcolor: alpha(PITCH_COLORS[pt] || theme.palette.primary.main, 0.04) },
                                borderRadius: 0.5,
                              }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 70 }}>
                                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', display: 'block', lineHeight: 1.2 }}>
                                    {pitchLabel(pt)}
                                  </Typography>
                                  <LinearProgress variant="determinate" value={pct} sx={{
                                    height: 3, borderRadius: 1, mt: 0.25,
                                    bgcolor: alpha(PITCH_COLORS[pt] || theme.palette.divider, 0.06),
                                    '& .MuiLinearProgress-bar': { bgcolor: PITCH_COLORS[pt] || theme.palette.primary.main, borderRadius: 1 },
                                  }} />
                                </Box>
                                <Typography variant="caption" fontFamily="monospace" fontWeight={600} sx={{ fontSize: '0.75rem', width: 24, textAlign: 'right' }}>
                                  {d.count}
                                </Typography>
                                <Typography variant="caption" fontFamily="monospace" fontWeight={600} sx={{ fontSize: '0.75rem', width: 34, textAlign: 'right', color: parseFloat(d.avgEV) >= 90 ? '#2ecc71' : 'text.secondary' }}>
                                  {d.avgEV}
                                </Typography>
                                <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.75rem', width: 28, textAlign: 'right', color: 'text.secondary' }}>
                                  {d.avgLA}°
                                </Typography>
                                <Box sx={{ width: 34, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                                  <Typography variant="caption" fontFamily="monospace" fontWeight={parseFloat(d.hardHitPct) >= 40 ? 700 : 400} sx={{
                                    fontSize: '0.75rem',
                                    color: parseFloat(d.hardHitPct) >= 40 ? '#2ecc71' : 'text.secondary',
                                  }}>
                                    {d.hardHitPct}%
                                  </Typography>
                                  {hhGrade && <GradeDot grade={hhGrade} />}
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    </Card>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {pitchData.total === 0 && (
            <Card variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No batted ball data found for this player
                {seasonType !== 'all' ? ` (${seasonType === 'S' ? 'Spring Training' : seasonType === 'P' ? 'Postseason' : 'Regular Season'})` : ''}
                {handFilter !== 'all' ? ` vs ${handFilter}HP` : ''}
                {outcomeFilter !== 'all' ? ` · outcome: ${outcomeFilter.replace('_', ' ')}` : ''}.
              </Typography>
            </Card>
          )}
        </motion.div>
      )}

      <AtBatDetailDialog
        open={abDialogOpen}
        onClose={() => setAbDialogOpen(false)}
        atBat={selectedAtBat}
        loading={abDialogLoading}
        error={abDialogError}
      />

      {/* Empty State (search-based only) */}
      {!selectedPlayer && !dataLoading && !isEmbedded && (
        <Card variant="outlined" sx={{
          borderRadius: 2, p: { xs: 3, sm: 5 }, textAlign: 'center',
          border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
        }}>
          <ScatterPlot sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1.5 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.05rem' }}>
            Search for a Player to Explore Batted Ball Data
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', display: 'block', mb: 2 }}>
            View individual batted ball locations, spray direction, exit velocity, launch angle, and quality-of-contact metrics.
            Filter by season type and opponent hand. Includes Statcast grades.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {['Spray Direction', 'Exit Velocity', 'Launch Angle', 'Barrel Rate', 'Hard Hit %'].map(tag => (
              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{
                height: 22, fontSize: '0.75rem', fontWeight: 600, opacity: 0.6,
              }} />
            ))}
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default SprayChartExplorer;
